"use client";

import React, { useEffect, useRef, useState } from "react";
import { FocusedImage } from "image-focus";
import type { JSX } from "react";
import Image from "next/image";
import { formatFocusArea } from "../../utils/formatFocusArea";
import { IMAGE_QUALITY_MOBILE } from "@/lib/heroImageUtils";

// const calcOffsetX = (x: number, width: number): number => {
//   // take pixel value for x axis from contentful and convert to correct format
//   const result: number = parseInt(((x / width) * 2 - 1).toFixed(2))
//   if (result > 1) {
//     // highest X value can be 1, so anything over should return 1
//     return 1
//   } else if (result < -1) {
//     // lowest X value can be -1, so anything under should return 1
//     return -1
//   } else {
//     return result
//   }
// }

// const calcOffsetY = (y: number, height: number): number => {
//   // take pixel value for y axis from contentful and convert to correct format
//   const result: number = parseInt(((y / height) * 2 - 1).toFixed(2)) * -1
//   if (result > 1) {
//     // highest Y value can be 1, so anything over should return 1
//     return 1
//   } else if (result < -1) {
//     // lowest Y value can be -1, so anything over should return 1
//     return -1
//   } else {
//     return result
//   }
// }

export interface imageSizesObject {
  desktop?: {
    width: number;
    height: number;
    ratio: {
      width: number;
      height: number;
    };
  };
  tablet?: {
    width: number;
    height: number;
    ratio: {
      width: number;
      height: number;
    };
  };
  mobile?: {
    width: number;
    height: number;
    ratio: {
      width: number;
      height: number;
    };
  };
}
export interface FocalPointImageProps {
  focalPointImage: {
    x?: number;
    y?: number;
    height?: number;
    width: number;
    url?: string;
    src?: string;
    title?: string;
    disableImageCompression?: boolean;
    focusArea?: string;
  };
  alt: string;
  imageSizes?: imageSizesObject;
  fullHeightWrapper?: boolean;
  pictureStyles?: object;
  preload?: boolean;
  quality: number;
  lazyLoad?: boolean;
  contentfulId?: string;
  tab?: boolean;
  className?: string;
  fillSizing?: boolean;
  // fetchPriority?: "high" | "auto" | "low",
  componentImagePriority?: "high" | "auto" | "low";
}
export const RenderFocusedImage = (props: FocalPointImageProps): JSX.Element => {
  const {
    focalPointImage,
    alt,
    imageSizes,
    fullHeightWrapper = false,
    pictureStyles,
    quality,
    lazyLoad = true,
    preload = false,
    // contentfulId,
    tab = false,
    className,
    fillSizing = false,
    componentImagePriority
  } = props;
  const effectiveFetchPriority = componentImagePriority ?? "low";
  const imageHeight = focalPointImage?.height || 0;
  const imageWidth = focalPointImage?.width || 0;
  const focusX = focalPointImage?.x || imageWidth / 2;
  const focusY = focalPointImage?.y || imageHeight / 2;
  const imageUrl = focalPointImage?.url || focalPointImage?.src || "";
  // breakpoints based on global scss vars: globalVars.$breakpoint-xs, globalVars.$breakpoint-sm, globalVars.$breakpoint-lg
  const breakPoints = {
    large: 1800,
    medium: 960,
    small: 640
  };
  const imageFocus = formatFocusArea(focalPointImage?.focusArea || "center");
  const loadSetting: "eager" | "lazy" = lazyLoad === false ? "eager" : "lazy";
  let imageQuality: number | null = IMAGE_QUALITY_MOBILE;
  if (focalPointImage?.disableImageCompression) {
    // if checkbox is checked in contentful, disable Contentful Image API compression and return the supplied image as-is.
    imageQuality = null;
  } else if (quality && typeof quality === "number") {
    imageQuality = quality;
  }
  const qualityQueryString = imageQuality != null ? "&q=" + imageQuality : "";
  const [_isLoaded, setLoaded] = useState(false);
  const computeInitialAspectRatio = (): string => {
    if (imageSizes?.desktop?.ratio) {
      return `${imageSizes.desktop.ratio.width} / ${imageSizes.desktop.ratio.height}`;
    }
    if (imageWidth != null && imageHeight != null) {
      return `${imageWidth} / ${imageHeight}`;
    }
    return "16 / 9";
  };
  const [aspectRatio, setAspectRatio] = useState(computeInitialAspectRatio);
  const wrapperElem: any = useRef(null);
  const imageElem: any = useRef(null);
  const focusedImageRef = useRef<any>(null);
  const calcWrapperAspectRatio = (width?: number, height?: number): string => {
    if (width && height) {
      // calculate aspect ratio from supplied manual values - for use in Luna components
      return `${width} / ${height}`;
    } else if (imageWidth != null && imageHeight != null) {
      // calculate aspect ratio from supplied image file values
      return `${imageWidth} / ${imageHeight}`;
    } else {
      // return a default value - 16:9
      return "16 / 9";
    }
  };
  const constructImageUrl = (imageWidth: number, imageHeight: number, format: string = "webp"): string => {
    if (imageWidth && imageHeight) {
      return imageUrl + `?fm=${format}&w=${imageWidth}&h=${imageHeight}&fit=fill` + qualityQueryString + `&f=${imageFocus}`;
    }
    if (imageHeight) {
      return imageUrl + `?fm=${format}&h=${imageHeight}` + qualityQueryString + `&f=${imageFocus}`;
    }
    return imageUrl + `?fm=${format}` + qualityQueryString + `&f=${imageFocus}`;
  };
  const imagesArray = {
    desktop2xImage: imageSizes?.desktop ? constructImageUrl(imageSizes.desktop.width * 2, imageSizes.desktop.height * 2) : null,
    desktop2xImageAvif: imageSizes?.desktop ? constructImageUrl(imageSizes.desktop.width * 2, imageSizes.desktop.height * 2, "avif") : null,
    desktopImage: imageSizes?.desktop ? constructImageUrl(imageSizes.desktop.width, imageSizes.desktop.height) : null,
    desktopImageAvif: imageSizes?.desktop ? constructImageUrl(imageSizes.desktop.width, imageSizes.desktop.height, "avif") : null,
    desktopImageRatio: imageSizes?.desktop?.ratio ? calcWrapperAspectRatio(imageSizes?.desktop?.ratio?.width, imageSizes?.desktop?.ratio?.height) : calcWrapperAspectRatio(),
    tabletImage: imageSizes?.tablet ? constructImageUrl(imageSizes.tablet.width, imageSizes.tablet.height) : null,
    tabletImageAvif: imageSizes?.tablet ? constructImageUrl(imageSizes.tablet.width, imageSizes.tablet.height, "avif") : null,
    tabletImageRatio: imageSizes?.tablet?.ratio ? calcWrapperAspectRatio(imageSizes?.tablet?.ratio?.width, imageSizes?.tablet?.ratio?.height) : calcWrapperAspectRatio(),
    mobileImage: imageSizes?.mobile ? constructImageUrl(imageSizes.mobile.width, imageSizes.mobile.height) : null,
    mobileImageAvif: imageSizes?.mobile ? constructImageUrl(imageSizes.mobile.width, imageSizes.mobile.height, "avif") : null,
    mobileImageRatio: imageSizes?.mobile?.ratio ? calcWrapperAspectRatio(imageSizes?.mobile?.ratio?.width, imageSizes?.mobile?.ratio?.height) : calcWrapperAspectRatio()
  };

  // Ref keeps the latest ratio values accessible from inside the ResizeObserver callback
  // without needing to teardown/recreate the observer on every render.
  const ratioValuesRef = useRef({
    mobileImageRatio: imagesArray.mobileImageRatio,
    tabletImageRatio: imagesArray.tabletImageRatio,
    desktopImageRatio: imagesArray.desktopImageRatio,
    fallbackRatio: calcWrapperAspectRatio()
  });
  ratioValuesRef.current = {
    mobileImageRatio: imagesArray.mobileImageRatio,
    tabletImageRatio: imagesArray.tabletImageRatio,
    desktopImageRatio: imagesArray.desktopImageRatio,
    fallbackRatio: calcWrapperAspectRatio()
  };

  // Initialize FocusedImage instance once on mount (not on every render).
  // The library's constructor reads geometric properties and sets up its own resize listener,
  // so creating it repeatedly was the primary source of forced reflows.
  // Hidden 26.03.2026, no longer being used - TO BE DEPRECATED
  // useEffect(() => {
  //   const img = imageElem?.current
  //   if (!img) return

  //   img.setAttribute(
  //     "data-focus-x",
  //     String(imageWidth != null ? calcOffsetX(focusX || 0, imageWidth) : 0),
  //   )
  //   img.setAttribute(
  //     "data-focus-y",
  //     String(imageHeight != null ? calcOffsetY(focusY || 0, imageHeight) : 0),
  //   )
  //   focusedImageRef.current = new FocusedImage(img)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [])

  // Use ResizeObserver for responsive aspect-ratio changes.
  // This replaces the old window resize + offsetWidth approach, eliminating forced reflows
  // because ResizeObserver provides the width via contentRect without triggering synchronous layout.
  useEffect(() => {
    const wrapper = wrapperElem?.current;
    if (!wrapper) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const width = entry.contentRect.width;
      const ratios = ratioValuesRef.current;
      let newRatio: string;
      if (width <= breakPoints.small && ratios.mobileImageRatio) {
        newRatio = ratios.mobileImageRatio;
      } else if (width <= breakPoints.medium && ratios.tabletImageRatio) {
        newRatio = ratios.tabletImageRatio;
      } else if (ratios.desktopImageRatio) {
        newRatio = ratios.desktopImageRatio;
      } else {
        newRatio = ratios.fallbackRatio;
      }
      setAspectRatio(prev => prev !== newRatio ? newRatio : prev);
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect image load — also re-applies focus shift once natural dimensions are available
  useEffect(() => {
    const imgElement = imageElem?.current;
    if (!imgElement) return;
    const handleLoad = () => {
      setLoaded(true);
      focusedImageRef.current?.applyShift();
    };
    if (imgElement.complete && imgElement.naturalHeight !== 0) {
      setLoaded(true);
      focusedImageRef.current?.applyShift();
    } else {
      imgElement.addEventListener("load", handleLoad);
    }
    return () => {
      imgElement.removeEventListener("load", handleLoad);
    };
  }, [imageUrl]);
  const returnImageSources = (): JSX.Element[] => {
    const sourcesArray = [];
    if (imagesArray?.mobileImageAvif) {
      sourcesArray?.push(<source type="image/avif" media="(max-width: 600px)" srcSet={imagesArray?.mobileImageAvif?.startsWith("http") ? imagesArray?.mobileImageAvif : `https:${imagesArray?.mobileImageAvif}`} />);
    }
    if (imagesArray?.mobileImage) {
      sourcesArray?.push(<source type="image/webp" media="(max-width: 600px)" srcSet={imagesArray?.mobileImage?.startsWith("http") ? imagesArray?.mobileImage : `https:${imagesArray?.mobileImage}`} />);
    }
    if (imagesArray?.tabletImageAvif) {
      sourcesArray?.push(<source type="image/avif" media="(max-width: 1200px)" srcSet={imagesArray?.tabletImageAvif?.startsWith("http") ? imagesArray?.tabletImageAvif : `https:${imagesArray?.tabletImageAvif}`} />);
    }
    if (imagesArray?.tabletImage) {
      sourcesArray?.push(<source type="image/webp" media="(max-width: 1200px)" srcSet={imagesArray?.tabletImage?.startsWith("http") ? imagesArray?.tabletImage : `https:${imagesArray?.tabletImage}`} />);
    }
    if (imagesArray?.desktop2xImageAvif && imagesArray?.desktopImageAvif) {
      sourcesArray?.push(<source type="image/avif" media={tab ? "" : "(max-width: 1800px"} srcSet={imagesArray?.desktopImageAvif?.startsWith("http") ? imagesArray?.desktopImageAvif : `https:${imagesArray?.desktopImageAvif}`} />, <source type="image/avif" srcSet={imagesArray?.desktop2xImageAvif?.startsWith("http") ? imagesArray?.desktop2xImageAvif : `https:${imagesArray?.desktop2xImageAvif}`} />);
    } else if (imagesArray?.desktopImageAvif) {
      sourcesArray?.push(<source type="image/avif" srcSet={imagesArray?.desktopImageAvif?.startsWith("http") ? imagesArray?.desktopImageAvif : `https:${imagesArray?.desktopImageAvif}`} />);
    }
    if (imagesArray?.desktopImage && imagesArray?.desktop2xImage) {
      sourcesArray?.push(<source type="image/webp" media={tab ? "" : "(max-width: 1800px"} srcSet={imagesArray?.desktopImage?.startsWith("http") ? imagesArray?.desktopImage : `https:${imagesArray?.desktopImage}`} />, <source type="image/webp" srcSet={imagesArray?.desktop2xImage?.startsWith("http") ? imagesArray?.desktop2xImage : `https:${imagesArray?.desktop2xImage}`} />);
    } else if (imagesArray?.desktopImage) {
      sourcesArray?.push(<source type="image/webp" srcSet={imagesArray?.desktopImage?.startsWith("http") ? imagesArray?.desktopImage : `https:${imagesArray?.desktopImage}`} />);
    }
    return sourcesArray;
  };
  const classFriendlyName = (name: string) => {
    // returns a 'slugified' name for targeting with focusedImage functionality below - this should be unique
    return name ? name.toLowerCase().replace(/[^\w ]+/g, " ").replace(/ +/g, "-") : "";
  };
  const imageName = focalPointImage?.title ? classFriendlyName(focalPointImage?.title) : "";
  const generatePlaceholder = (imageUrl: string, forceFull: boolean = false) => {
    // If we have no responsive sources (natural mode), use the full image immediately to avoid blurry upscale.
    if (forceFull) {
      return imageUrl.startsWith("http") ? imageUrl + (qualityQueryString ? imageQuality != null ? `?fm=jpg${qualityQueryString}` : "" : "") : "https:" + imageUrl + (qualityQueryString ? imageQuality != null ? `?fm=jpg${qualityQueryString}` : "" : "");
    }
    // Otherwise generate a lightweight placeholder.
    return imageUrl.startsWith("http") ? imageUrl + "?fm=jpg&fl=progressive&w=50&h=50" + qualityQueryString : "https:" + imageUrl + "?fm=jpg&fl=progressive&w=50&h=50" + qualityQueryString;
  };
  const containerStyles = Object.assign({
    aspectRatio: aspectRatio,
    height: fullHeightWrapper ? "100%" : "auto",
    ...(fillSizing ? {
      position: "relative" as const
    } : {})
  }, pictureStyles);
  if (focalPointImage) {
    const sources = returnImageSources();
    const noSources = sources.length === 0; // natural mode (no imageSizes passed)
    const fetchPrioritySetting: "high" | "auto" | "low" = effectiveFetchPriority ? effectiveFetchPriority : preload ? "high" : "auto";
    // In "natural mode" (no imageSizes passed, so no <source> breakpoints
    // exist) the fallback <Image> is the actual resource the browser
    // fetches. Passing raw natural width/height with no `sizes` makes
    // Next.js treat it as fixed-size at the FULL natural resolution,
    // generating up to a 3840px candidate regardless of display size.
    // Capping `sizes` keeps that fetch proportional to what's rendered.
    const sizesSetting = fillSizing ? {
      fill: true as const,
      sizes: "100vw"
    } : {
      width: imageWidth || 0,
      height: imageHeight || 0,
      ...(noSources ? {
        sizes: "(max-width: 640px) 100vw, 50vw"
      } : {})
    };
    const placeholderUrl = generatePlaceholder(imageUrl, noSources) + `&f=${imageFocus}`;
    const imageComponentStyle = fillSizing ? {
      opacity: 1,
      objectFit: "cover" as const
    } : {
      opacity: 1
    };
    const imageComponentProps = {
      loading: loadSetting,
      ref: imageElem,
      ...sizesSetting,
      // Use original full image immediately if we have no generated sources to avoid blurry placeholder stretch.
      src: placeholderUrl,
      alt: alt ? alt : "",
      className: `ter-focused-image ${"focused-image-" + imageName}`,
      style: {
        ...imageComponentStyle
      },
      fetchPriority: fetchPrioritySetting
      // preload: preload,
    };
    return <picture ref={wrapperElem} className={`ter-focused-image-container block w-full ${className ? className : ""}`} style={containerStyles}>
        {sources && sources.map((item, i) => <React.Fragment key={i}>{item}</React.Fragment>)}
        <Image {...imageComponentProps} />
      </picture>;
  } else {
    return <></>;
  }
};